import type { createApplicationStartup } from '../../../shared/application-startup';

interface BootstrapOptions {
  document: Document;
  root: HTMLElement;
  startup: ReturnType<typeof createApplicationStartup>;
  loadingText: () => string;
  failureText: () => string;
  retryText: () => string;
  retry: () => void;
  unsupportedText: () => {
    title: string;
    description: string;
    browsers: string;
    requirement: string;
    safari: string;
  };
}

/** Keeps React mounted at its real size beneath the existing startup screen. */
export const createBrowserBootstrap = ({
  document, root, startup, loadingText, failureText, retryText, retry, unsupportedText,
}: BootstrapOptions) => {
  const shell = document.getElementById('app-bootstrap')!;
  let revision = 0;

  const show = (message: string, onRetry?: () => void) => {
    revision += 1;
    document.documentElement.dataset.appLoading = 'true';
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('inert', '');
    shell.hidden = false;
    shell.className = 'app-bootstrap';
    shell.replaceChildren();
    const mark = document.createElement('div');
    mark.className = 'app-bootstrap__mark';
    mark.textContent = '☰';
    mark.setAttribute('aria-hidden', 'true');
    const text = document.createElement('p');
    text.textContent = message;
    shell.append(mark, text);
    if (onRetry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = retryText();
      button.addEventListener('click', onRetry, { once: true });
      shell.append(button);
    }
  };

  const showUnsupported = () => {
    const copy = unsupportedText();
    show('');
    shell.className = 'app-bootstrap app-bootstrap--unsupported';
    const title = document.createElement('h1');
    title.textContent = copy.title;
    const description = document.createElement('p');
    description.textContent = copy.description;
    const label = document.createElement('h2');
    label.textContent = copy.browsers;
    const browsers = document.createElement('ul');
    for (const name of ['Google Chrome', 'Microsoft Edge', 'Mozilla Firefox', copy.safari]) {
      const item = document.createElement('li');
      item.textContent = name;
      browsers.append(item);
    }
    const requirement = document.createElement('p');
    requirement.className = 'app-bootstrap__detail';
    requirement.textContent = copy.requirement;
    const address = document.createElement('p');
    address.className = 'app-bootstrap__address';
    address.textContent = 'oracle.neiro.network';
    shell.append(title, description, label, browsers, requirement, address);
  };

  const sync = () => {
    const status = startup.getStatus();
    if (status === 'unsupported') {
      showUnsupported();
      return;
    }
    if (status !== 'ready') {
      show(status === 'failed' ? failureText() : loadingText(), status === 'failed' ? retry : undefined);
      return;
    }
    const expectedRevision = ++revision;
    // Fonts and two layout/paint turns must settle after the last startup task.
    Promise.resolve(document.fonts?.ready).then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (expectedRevision !== revision || startup.getStatus() !== 'ready') return;
        startup.markRevealed();
        delete document.documentElement.dataset.appLoading;
        root.removeAttribute('aria-hidden');
        root.removeAttribute('inert');
        shell.hidden = true;
      }));
    }).catch(() => {
      if (expectedRevision === revision) show(failureText(), retry);
    });
  };

  const unsubscribe = startup.subscribe(sync);
  return { show, dispose: () => { revision += 1; unsubscribe(); } };
};
